import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    Injector,
    OnInit,
    Signal,
    signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { SecurityService } from '@connect-services/client/services/security.service';
import { ProjectControllerGet200Response, ProjectsService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingComponent, RessourcesModule],
    selector: 'app-project-redirect',
    templateUrl: './project-redirection.component.html',
})
export class ProjectRedirectionComponentV2 implements OnInit {
    public readonly hasPermissions: Signal<boolean> = computed((): boolean => {
        if (!this.securityService.meSignal()?.role?.permissionAliases) {
            return false;
        }

        return (
            this.securityService.meSignal()!.role!.permissionAliases.includes('manage_Project') ||
            this.securityService.meSignal()!.role!.permissionAliases.includes('read_Project')
        );
    });
    public readonly hasProjects: Signal<boolean> = computed((): boolean => {
        if (!this.securityService.meSignal() || this.isLoading()) {
            return false;
        }

        try {
            return !!this.projects()?.items.length;
        } catch {
            return false;
        }
    });
    public readonly isLoading = signal<boolean>(true);
    public readonly projects = signal<ProjectControllerGet200Response | null>(null);

    protected readonly securityService = inject(SecurityService);

    private readonly projectsService = inject(ProjectsService);
    private readonly router = inject(Router);

    public constructor(private readonly injector: Injector) {}

    public getProjects(): Promise<ProjectControllerGet200Response> {
        return firstValueFrom(
            this.projectsService.projectControllerGet({
                isCaseSensitive: true,
                orderBy: 'name',
                person: this.securityService.meSignal()?.id,
            }),
        );
    }

    public ngOnInit(): void {
        effect(
            async (): Promise<void> => {
                this.isLoading.set(true);
                this.projects.set(await this.getProjects());
                this.isLoading.set(false);
            },
            { injector: this.injector },
        );

        effect(
            async (): Promise<void> => {
                if (
                    !this.isLoading() &&
                    this.securityService.meSignal() &&
                    this.hasPermissions() &&
                    this.hasProjects()
                ) {
                    await this.router.navigate([RoutePathEnum.PROJECTS, this.projects()?.items[0].id]);

                    return;
                }
                if (
                    !this.isLoading() &&
                    this.securityService.meSignal() &&
                    this.hasPermissions() &&
                    !this.hasProjects()
                ) {
                    await this.router.navigate([RoutePathEnum.PROJECTS, RoutePathEnum.HOME]);
                }
            },
            { injector: this.injector },
        );
    }
}
